






















import Vue, { PropType } from 'vue';
import {
  MStoreFormDatePickerPropertiesModel,
  MStoreFormInputPropertiesModel,
  MStoreFormLookupPropertiesModel,
  SchemaModel,
  MStoreUnsupportedInputPropertiesModel,
  MStoreFormSelectTablePropertiesModel,
  HtmlDomEvent,
  Dictionary
} from '@/types';
import MStoreFormInput from '@/components/shared/MStoreFormInput.vue';
import MStoreUnsupportedInput from '@/components/shared/MStoreUnsupportedInput.vue';
import MStoreFormSelectTable from '@/components/shared/MStoreFormSelectTable.vue';
import MStoreFormLookup from '@/components/shared/MStoreFormLookup.vue';
import MStoreFormDatePicker from '@/components/shared/MStoreFormDatePicker.vue';
import MStoreFormCheckbox from './MStoreFormCheckbox.vue';
import { ComponentHelper } from '@/mixins/componentHelper';

export default Vue.extend({
  props: {
    value: {
      type: Array as () => SchemaModel[],
      default: Array as () => SchemaModel[]
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    isReadonly: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    fieldData: {
      type: Object as PropType<Dictionary<any>>
    },
    preventUpdateOfApplicationDirty: {
      type: Boolean,
      default: false
    }
  },
  mixins: [ComponentHelper],
  methods: {
    getComponentName(this: any, schema: SchemaModel) {
      const registeredComponents = ['MStoreFormInput', 'MStoreFormSelectTable', 'MStoreFormLookup', 'MStoreFormDatePicker', 'MStoreUnsupportedInput', 'MStoreFormCheckbox'];
      return registeredComponents.find((x) => x === schema.componentProperties?.componentName) || 'MStoreUnsupportedInput';
    },
    getVueBindings(this: any, schema: SchemaModel, index: number) {
      const readonly = schema.readonly || this.isReadonly;
      switch (schema.componentProperties?.componentName) {
        case 'MStoreFormInput': {
          const componentProperties = schema.componentProperties as MStoreFormInputPropertiesModel;
          return {
            id: schema.id,
            value: schema.value,
            readonly,
            label: schema.label,
            isDisabled: schema.isDisabled,
            autocomplete: componentProperties.autocomplete,
            lazyFormatter: componentProperties.lazyFormatter,
            placeholder: componentProperties.placeholder,
            type: componentProperties.type,
            forceUpperCase: componentProperties.forceUpperCase,
            size: this.small ? 'sm' : ''
          };
        }
        case 'MStoreFormSelectTable': {
          const componentProperties = schema.componentProperties as MStoreFormSelectTablePropertiesModel;
          return {
            data: schema.value,
            list: componentProperties.list,
            isLoading: this.isLoading,
            fieldLabel: schema.label,
            fieldId: schema.id,
            filterDisabled: componentProperties.filterDisabled,
            isReadonly: readonly,
            displayField: componentProperties.displayField,
            valueField: componentProperties.valueField,
            ignoreIsRequired: componentProperties.ignoreIsRequired,
            actionButtonComponentName: componentProperties.actionButtonComponentName || '',
            actionButtonState: componentProperties.actionButtonState,
            formId: componentProperties.formId,
            isActionButtonDisabled: componentProperties.isActionButtonDisabled,
            lineUpOnType: componentProperties.lineUpOnType,
            allowTypedValue: componentProperties.allowTypedValue,
            maxItemsBeforeScroll: 10,
            top: componentProperties.top,
            size: this.small ? 'sm' : ''
          };
        }
        case 'MStoreFormLookup': {
          const componentProperties = schema.componentProperties as MStoreFormLookupPropertiesModel;
          return {
            data: schema.value,
            listId: componentProperties.listId,
            isLoading: this.isLoading,
            fieldLabel: schema.label,
            fieldId: schema.id,
            isReadonly: readonly,
            hasInitialList: componentProperties.hasInitialList,
            actionButtonComponentName: componentProperties.actionButtonComponentName || '',
            actionButtonState: componentProperties.actionButtonState,
            ignoreIsRequired: componentProperties.ignoreIsRequired,
            formId: componentProperties.formId,
            filterDisabled: componentProperties.filterDisabled,
            keyFilterValues: this.allFieldData,
            allowTypedValue: componentProperties.allowTypedValue,
            maxItemsBeforeScroll: 10,
            size: this.small ? 'sm' : ''
          };
        }
        case 'MStoreFormDatePicker': {
          const componentProperties = schema.componentProperties as MStoreFormDatePickerPropertiesModel;
          return {
            id: schema.id,
            value: schema.value,
            label: schema.label,
            lang: componentProperties.language,
            format: componentProperties.format,
            placeholder: componentProperties.placeholder,
            readonly,
            isDisabled: schema.isDisabled,
            valueType: componentProperties.valueType,
            size: this.small ? 'sm' : ''
          };
        }
        case 'MStoreFormCheckbox': {
          return {
            id: schema.id,
            value: schema.value,
            label: schema.label,
            isDisabled: readonly,
            size: this.small ? 'sm' : ''
          };
        }
        default: {
          const componentProperties = schema.componentProperties as MStoreUnsupportedInputPropertiesModel;
          return {
            htmlElementId: `unsupported_${schema.id}`,
            fieldLabel: schema.label,
            message: componentProperties.unsupportedMessage || 'The requested input method is unsupported'
          };
        }
      }
    },
    getVueEventHandlers(this: any, schema: SchemaModel, index: number) {
      switch (schema.componentProperties?.componentName) {
        case 'MStoreFormInput': {
          return {
            [HtmlDomEvent.Input]: ($event: any) => this.updateValue($event, schema, index, false),
            [HtmlDomEvent.Blur]: ($event: any) => this.updateValue($event, schema, index)
          };
        }
        case 'MStoreFormSelectTable':
        case 'MStoreFormLookup': {
          return {
            'update:data': ($event: any) => this.updateValue($event, schema, index)
          };
        }
        case 'MStoreFormDatePicker': {
          return {
            [HtmlDomEvent.Input]: ($event: any) => this.updateValue($event, schema, index)
          };
        }
        case 'MStoreFormCheckbox': {
          return {
            [HtmlDomEvent.Input]: ($event: any) => this.updateValue($event, schema, index)
          };
        }
        default: {
          return {};
        }
      }
    },
    updateValue(this: any, newValue: any, schema: SchemaModel, indexOfSchema: number, emitUpdateField: boolean = true) {
      if (schema.readonly || schema.isDisabled) {
        return;
      }
      const updatedSchemaArray = [...this.value] as SchemaModel[];
      const updatedSchema = { ...schema, value: newValue };
      Vue.set(updatedSchemaArray, indexOfSchema, updatedSchema);
      this.$emit(`update:${schema.id}`, newValue);
      this.$emit(HtmlDomEvent.Input, updatedSchemaArray);
      if (emitUpdateField) {
        this.$emit('update:field', updatedSchema);
      }
    },
    setFocusOnField(this: any, field: SchemaModel) {
      this.focusElementById(field.id);
    },
    onDirtyForm(this: any, isDirty: boolean) {
      this.$emit('formDirty', isDirty);
    }
  },
  computed: {
    allFieldData(this: any): any {
      if (this.fieldData) {
        return this.fieldData;
      }
      const formData = {} as Dictionary<any>;
      this.value.forEach((field: SchemaModel) => {
        formData[field.id] = field.value;
      });
      return formData;
    }
  },
  components: {
    /* When adding a new component the name of the component must also be added to
    the registeredComponents array in the getComponentName method */
    MStoreFormInput,
    MStoreUnsupportedInput,
    MStoreFormSelectTable,
    MStoreFormLookup,
    MStoreFormDatePicker,
    MStoreFormCheckbox
  }
});
